import { ALTERNATIVE_DATES_EVENTS_BY_CATEGORY } from './alternativeDatesEventNames';
import { SEARCH_CONTROLS_EVENTS_BY_CATEGORY } from './searchControlsEventNames';

type EventCategory =
  | 'AUTOSUGGEST'
  | 'BEHAVIOURAL'
  | 'CHOKE_POINTS'
  | 'FUNNEL'
  | 'INTERACTION'
  | 'MINIEVENT_MISC'
  | 'MIXED'
  | 'UI'
  | 'SMART_METRICS';

type Event = {
  NAME: string;
  OWNER:
    | 'Arcane'
    | 'Artemis'
    | 'Interstellar'
    | 'BabelFish'
    | 'Capybara'
    | 'Greyfriars'
    | 'Compass'
    | 'Coral'
    | 'Goose'
    | 'Hermes'
    | 'IrnBru'
    | 'IrnBru/Theseus'
    | 'Luna'
    | 'Juno'
    | 'chAI'
    | 'Potato'
    | 'Quark'
    | 'Sentinel'
    | 'Shuttle/Woodpecker'
    | 'Stargate'
    | 'Tuxedo'
    | 'Theseus'
    | 'Graviton';
  CONTEXT?: string;
};

export type NamesFromCategory<C extends keyof typeof ANALYTICS_EVENTS> = Extract<
  (typeof ANALYTICS_EVENTS)[C][keyof (typeof ANALYTICS_EVENTS)[C]],
  { NAME: string }
>['NAME'];

export const ANALYTICS_EVENTS = {
  INTERACTION: {
    BP_PQS_SUMMARY: {
      NAME: 'bp_pqs_summary',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'Booking Panel PQS Summary Sheet',
    },

    // Book It For Me (BIFM) Events
    BIFM_QUICKBOOK_MODAL_RENDERED: {
      NAME: 'bifm_quickbook_modal_rendered',
      OWNER: 'Juno',
      CONTEXT: 'When the BIFM QuickBook modal intro screen is rendered',
    },
    BIFM_QUICKBOOK_BTN_SELECTED: {
      NAME: 'bifm_quickbook_btn_selected',
      OWNER: 'Juno',
      CONTEXT: 'When a traveller selects the QuickBook button in the BIFM intro screen',
    },
    BIFM_TRIGGER_SELECTED: {
      NAME: 'bifm_trigger_selected',
      OWNER: 'Juno',
      CONTEXT: 'When a traveller clicks the Book It For Me button on the booking panel agent CTA',
    },
    BIFM_EXIT: {
      NAME: 'bifm_quit_selected',
      OWNER: 'Juno',
      CONTEXT: 'When the user clicks the Quit button in the BIFM header',
    },
    BIFM_QUIT_CONFIRMATION_RENDERED: {
      NAME: 'bifm_quit_confirmation_rendered',
      OWNER: 'Juno',
      CONTEXT: 'When the BIFM quit confirmation screen is rendered',
    },
    BIFM_STOP_AND_GO_TO_PARTNER_SELECTED: {
      NAME: 'bifm_stop_and_go_to_partner_selected',
      OWNER: 'Juno',
      CONTEXT: 'When user has clicked on quit button and selected the go to partner button',
    },
    BIFM_RETURN_TO_SKYSCANNER_SELECTED: {
      NAME: 'bifm_return_to_skyscanner_selected',
      OWNER: 'Juno',
      CONTEXT:
        'When the user exits the BIFM flow and returns to the Skyscanner booking panel (e.g. when no partner deeplink is available after quitting)',
    },
    BIFM_BOOKING_COMPLETE: {
      NAME: 'bifm_booking_complete',
      OWNER: 'Juno',
      CONTEXT: 'When booking is complete',
    },
    BIFM_GO_TO_PARTNER_SELECTED: {
      NAME: 'bifm_go_to_partner_selected',
      OWNER: 'Juno',
      CONTEXT: 'When the QuickBook modal is displayed and user selects the Go to Provider button',
    },
    BIFM_INTRO_MODAL_DISMISSED: {
      NAME: 'bifm_intro_modal_dismissed',
      OWNER: 'Juno',
      CONTEXT: 'When the traveller dismisses the BIFM intro modal via the close button',
    },
    BIFM_TRAVELLER_DETAILS_SUBMITTED: {
      NAME: 'bifm_traveller_details_submitted',
      OWNER: 'Juno',
      CONTEXT: 'When the traveller submits their details in the BIFM traveller form',
    },
    BIFM_BACK_TO_PASSENGER_DETAILS_SELECTED: {
      NAME: 'bifm_back_to_passenger_details_selected',
      OWNER: 'Juno',
      CONTEXT: 'When the traveller clicks Back to passenger details from the BIFM payment form',
    },
    BIFM_RATING: {
      NAME: 'bifm_rating',
      OWNER: 'Juno',
      CONTEXT: 'When the user rates using the stars in the BIFM booking complete screen',
    },
    BIFM_CONFIRM_AND_PAY_SELECTED: {
      NAME: 'bifm_confirm_and_pay_selected',
      OWNER: 'Juno',
      CONTEXT: 'When the user clicks the Continue button on the BIFM payment form',
    },
    BIFM_PAYMENT_DETAILS_SUBMITTED: {
      NAME: 'bifm_payment_details_submitted',
      OWNER: 'Juno',
      CONTEXT: 'When the traveller submits their details in the BIFM payment form',
    },
    BIFM_AGENT_PROGRESS_DISPLAYED: {
      NAME: 'bifm_agent_progress_displayed',
      OWNER: 'Juno',
      CONTEXT: 'When the agent progress is displayed',
    },
    BIFM_PAYMENT_METHOD_TIMEOUT: {
      NAME: 'bifm_payment_method_timeout',
      OWNER: 'Juno',
      CONTEXT: 'UPI QR code timer expired in BifmPaymentSelection',
    },
    BIFM_GENERIC_ERROR_SHOWN: {
      NAME: 'bifm_generic_error_shown',
      OWNER: 'Juno',
      CONTEXT: 'When the BIFM generic error screen is displayed',
    },
    // ExploreAI Events
    EXPLORE_AI_LOADED: {
      NAME: 'Explore_AI_Loaded',
      OWNER: 'chAI',
      CONTEXT: 'Explore AI page loaded.',
    },
    EXPLORE_AI_SEARCH_SUBMITTED: {
      NAME: 'Explore_AI_Search_Submitted',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller submits an Explore AI search.',
    },
    EXPLORE_AI_RESULTS_RENDERED: {
      NAME: 'Explore_AI_Results_Rendered',
      OWNER: 'chAI',
      CONTEXT: 'When Explore AI results are successfully returned from the AskSkyscanner API.',
    },
    EXPLORE_AI_TIME_TO_FIRST_LOADING_MESSAGE: {
      NAME: 'Explore_AI_Time_To_First_Loading_Message',
      OWNER: 'chAI',
      CONTEXT: 'Latency from search submission to first loading message from ask-skyscanner.',
    },
    EXPLORE_AI_TIME_TO_RESULTS_RECEIVED: {
      NAME: 'Explore_AI_Time_To_Results_Received',
      OWNER: 'chAI',
      CONTEXT: 'Latency from search submission to results received from ask-skyscanner.',
    },
    EXPLORE_AI_CARD_CLICKED: {
      NAME: 'Explore_AI_Card_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks on a result card in Explore AI.',
    },
    EXPLORE_AI_CITY_PILL_CLICKED: {
      NAME: 'Explore_AI_City_Pill_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks a city pill on a country card in Traveller Insights.',
    },
    EXPLORE_AI_UNSUPPORTED_INTENT_BANNER_SHOWN: {
      NAME: 'Explore_AI_Unsupported_Intent_Banner_Shown',
      OWNER: 'chAI',
      CONTEXT:
        'When Explore AI renders an unsupported intent banner (hotels, car hire, packages, etc.).',
    },
    EXPLORE_AI_ERROR_BANNER_SHOWN: {
      NAME: 'Explore_AI_Error_Banner_Shown',
      OWNER: 'chAI',
      CONTEXT: 'When an error banner is shown in Explore AI.',
    },
    EXPLORE_AI_ERROR_CTA_CLICK: {
      NAME: 'Explore_AI_Error_CTA_Click',
      OWNER: 'chAI',
      CONTEXT: 'When a user clicks the CTA link in an Explore AI error banner.',
    },
    EXPLORE_AI_UNSUPPORTED_INTENT_CTA_CLICK: {
      NAME: 'Explore_AI_Unsupported_Intent_CTA_Click',
      OWNER: 'chAI',
      CONTEXT: 'When a user clicks a CTA link in an Explore AI unsupported intent banner.',
    },
    EXPLORE_AI_FEEDBACK_BUTTON_IN_VIEWPORT: {
      NAME: 'Explore_AI_Feedback_Button_In_Viewport',
      OWNER: 'chAI',
      CONTEXT: 'When the Give Feedback button enters the viewport in Traveller Insights results.',
    },
    EXPLORE_AI_FEEDBACK_SUBMITTED: {
      NAME: 'Explore_AI_Feedback_Submitted',
      OWNER: 'chAI',
      CONTEXT: 'When a user submits feedback in Explore AI.',
    },
    EXPLORE_AI_FIND_FLIGHT_CLICKED: {
      NAME: 'Explore_AI_Find_Flight_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks the Find flights button on a Traveller Insights city card.',
    },
    EXPLORE_AI_WHEN_TO_GO_FLIGHT_CLICKED: {
      NAME: 'Explore_AI_When_To_Go_Flight_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks a flight price link in the When To Go table.',
    },
    EXPLORE_AI_WHEN_TO_GO_TABLE_TOGGLED: {
      NAME: 'Explore_AI_When_To_Go_Table_Toggled',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller expands or collapses the When To Go table.',
    },
    EXPLORE_AI_ORIGIN_SELECTOR_OPENED: {
      NAME: 'Explore_AI_Origin_Selector_Opened',
      OWNER: 'chAI',
      CONTEXT: 'When a user opens the origin selector.',
    },
    EXPLORE_AI_ORIGIN_AUTOSUGGEST_SELECTED: {
      NAME: 'Explore_AI_Origin_Autosuggest_Selected',
      OWNER: 'chAI',
      CONTEXT: 'When a user selects an origin from the autosuggest list.',
    },
    EXPLORE_AI_PRICE_GRAPH_CLICKED: {
      NAME: 'Explore_AI_Price_Graph_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks a month bar on the price graph in a city card.',
    },
    EXPLORE_AI_PRICE_GRAPH_HOVERED: {
      NAME: 'Explore_AI_Price_Graph_Bar_Hovered',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller hovers over a month bar on the price graph in a city card.',
    },
    EXPLORE_AI_PRICE_GRAPH_CONTROL_CLICKED: {
      NAME: 'Explore_AI_Price_Graph_Control_Clicked',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks the previous or next navigation control on the price graph in a city card.',
    },
    EXPLORE_AI_VOICE_INPUT_CLICKED: {
      NAME: 'Explore_AI_Voice_Input_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks the microphone button.',
    },
    EXPLORE_AI_VOICE_RECORDING_STARTED: {
      NAME: 'Explore_AI_Voice_Recording_Started',
      OWNER: 'chAI',
      CONTEXT: 'When the browser grants mic access and recording begins.',
    },
    EXPLORE_AI_NUDGE_SHOWN: {
      NAME: 'Explore_AI_Nudge_Shown',
      OWNER: 'chAI',
      CONTEXT: 'When a nudge was shown to the traveller.',
    },
    EXPLORE_AI_NUDGE_SUGGESTION_CLICKED: {
      NAME: 'Explore_AI_Nudge_Suggestion_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a nudge suggestion was clicked by the traveller.',
    },
    EXPLORE_AI_NUDGE_FAILURE: {
      NAME: 'Explore_AI_Nudge_Failure',
      OWNER: 'chAI',
      CONTEXT: 'When the nudge request fails.',
    },
    EXPLORE_AI_AUTOSUGGEST_ORIGIN_FAILURE: {
      NAME: 'Explore_AI_Autosuggest_Origin_Failure',
      OWNER: 'chAI',
      CONTEXT: 'When the autosuggest request fails for origin selection.',
    },
    EXPLORE_AI_MODAL_CLOSED: {
      NAME: 'Explore_AI_Modal_Closed',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller closes a city card modal in Traveller Insights (mobile or grid layout).',
    },
    EXPLORE_AI_EXPLORE_CARD_CLICKED: {
      NAME: 'Explore_AI_Explore_Card_Clicked',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks the Explore button on a Traveller Insights city or country card.',
    },
    EXPLORE_AI_COMPARE_BACK_BUTTON_CLICKED: {
      NAME: 'Explore_AI_Compare_Back_Button_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks the back button to exit compare mode.',
    },
    EXPLORE_AI_COMPARE_BUTTON_CLICKED: {
      NAME: 'Explore_AI_Compare_Button_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks the compare button in Traveller Insights.',
    },
    EXPLORE_AI_COMPARE_CHECKBOX_CLICKED: {
      NAME: 'Explore_AI_Compare_Checkbox_Clicked',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks a compare checkbox on a Traveller Insights city or country card.',
    },
    EXPLORE_AI_NO_RESULTS_SUGGESTION_CLICKED: {
      NAME: 'Explore_AI_No_Results_Suggestion_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a suggestion link is clicked on the no-results error banner.',
    },
    EXPLORE_AI_FLIGHT_RESULTS_SECTION_LOADED: {
      NAME: 'Explore_AI_Flight_Results_Section_Loaded',
      OWNER: 'chAI',
      CONTEXT: 'When the flight results section renders with flight month cards.',
    },
    EXPLORE_AI_FLIGHT_RESULTS_SECTION_IN_VIEWPORT: {
      NAME: 'Explore_AI_Flight_Results_Section_In_Viewport',
      OWNER: 'chAI',
      CONTEXT: 'When the flight results section enters the viewport.',
    },
    EXPLORE_AI_FLIGHT_RESULTS_SHOW_MORE_CLICKED: {
      NAME: 'Explore_AI_Flight_Results_Show_More_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks Show More to expand the flight results on mobile.',
    },
    EXPLORE_AI_FLIGHT_RESULTS_SHOW_LESS_CLICKED: {
      NAME: 'Explore_AI_Flight_Results_Show_Less_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks Show Less to collapse the flight results on mobile.',
    },
    EXPLORE_AI_FLIGHT_CHART_LOADED: {
      NAME: 'Explore_AI_Flight_Chart_Loaded',
      OWNER: 'chAI',
      CONTEXT: 'When the flight price chart finishes loading, with or without prices.',
    },
    EXPLORE_AI_FLIGHT_CHART_IN_VIEWPORT: {
      NAME: 'Explore_AI_Flight_Chart_In_Viewport',
      OWNER: 'chAI',
      CONTEXT: 'When the flight price chart bars enter the viewport.',
    },
    EXPLORE_AI_FLIGHT_INSIGHTS_PANEL_LOADED: {
      NAME: 'Explore_AI_Flight_Insights_Panel_Loaded',
      OWNER: 'chAI',
      CONTEXT: 'When the flight insights panel renders with at least one insight.',
    },
    EXPLORE_AI_FLIGHT_INSIGHTS_PANEL_NOT_AVAILABLE: {
      NAME: 'Explore_AI_Flight_Insights_Panel_Not_Available',
      OWNER: 'chAI',
      CONTEXT: 'When the flight insights panel has no insights to show.',
    },
    EXPLORE_AI_FLIGHT_INSIGHTS_PANEL_IN_VIEWPORT: {
      NAME: 'Explore_AI_Flight_Insights_Panel_In_Viewport',
      OWNER: 'chAI',
      CONTEXT: 'When the flight insights panel enters the viewport.',
    },
    EXPLORE_AI_TRIP_LENGTH_ADJUSTED: {
      NAME: 'Explore_AI_Trip_Length_Adjusted',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks the increase or decrease trip length button in the trip selector.',
    },
    EXPLORE_AI_TRIP_SELECTOR_FIND_FLIGHTS_CLICKED: {
      NAME: 'Explore_AI_Trip_Selector_Find_Flights_Clicked',
      OWNER: 'chAI',
      CONTEXT: 'When a traveller clicks the Find Flights CTA in the trip selector.',
    },
    EXPLORE_AI_FLIGHT_CHART_BAR_CLICKED: {
      NAME: 'Explore_AI_Flight_Chart_Bar_Clicked',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks a day bar in the flight price chart to select a start date.',
    },
    EXPLORE_AI_FLIGHT_CHART_MONTH_CHANGED: {
      NAME: 'Explore_AI_Flight_Chart_Month_Changed',
      OWNER: 'chAI',
      CONTEXT:
        'When a traveller clicks the previous or next month button in the flight price chart.',
    },
    EXPLORE_AI_DATE_PICKER_OPENED: {
      NAME: 'Explore_AI_Date_Picker_Opened',
      OWNER: 'chAI',
      CONTEXT: 'When a user opens a date picker in Explore AI.',
    },
    EXPLORE_AI_DATE_SELECTED: {
      NAME: 'Explore_AI_Date_Selected',
      OWNER: 'chAI',
      CONTEXT: 'When a user selects a date in Explore AI.',
    },
    EXPLORE_AI_LOCATION_PICKER_OPENED: {
      NAME: 'Explore_AI_Location_Picker_Opened',
      OWNER: 'chAI',
      CONTEXT: 'When a user opens an origin or destination picker.',
    },
    EXPLORE_AI_LOCATION_SELECTED: {
      NAME: 'Explore_AI_Location_Selected',
      OWNER: 'chAI',
      CONTEXT: 'When a user selects an origin or destination from a location picker.',
    },

    // City Drawer Events
    CITY_DRAWER_OPENED: {
      NAME: 'CITY_DRAWER_OPENED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer is opened by the user.',
    },
    CITY_DRAWER_LOCATION_TAGS_FAILED: {
      NAME: 'CITY_DRAWER_LOCATION_TAGS_FAILED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer location tags component fails to load.',
    },
    CITY_DRAWER_INDICATIVE_PRICING_LOADED: {
      NAME: 'CITY_DRAWER_INDICATIVE_PRICING_LOADED',
      OWNER: 'Potato',
      CONTEXT:
        'When the City Drawer indicative pricing component loads successfully with flight and/or hotel prices.',
    },
    CITY_DRAWER_INDICATIVE_PRICING_VIEWED: {
      NAME: 'CITY_DRAWER_INDICATIVE_PRICING_VIEWED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer indicative pricing component is viewed by the user.',
    },
    CITY_DRAWER_IMAGE_CAROUSEL_LOADED: {
      NAME: 'CITY_DRAWER_IMAGE_CAROUSEL_LOADED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer image carousel component loads successfully with images.',
    },
    CITY_DRAWER_IMAGE_CAROUSEL_FAILED: {
      NAME: 'CITY_DRAWER_IMAGE_CAROUSEL_FAILED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer image carousel component fails to load due to missing data.',
    },
    CITY_DRAWER_IMAGE_CAROUSEL_VIEWED: {
      NAME: 'CITY_DRAWER_IMAGE_CAROUSEL_VIEWED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer image carousel component is viewed by the user.',
    },
    CITY_DRAWER_CHEAPEST_TIME_TO_FLY_LOADED: {
      NAME: 'CITY_DRAWER_CHEAPEST_TIME_TO_FLY_LOADED',
      OWNER: 'Potato',
      CONTEXT:
        'When the City Drawer cheapest time to fly component loads successfully with monthly pricing data.',
    },
    CITY_DRAWER_CHEAPEST_TIME_TO_FLY_FAILED: {
      NAME: 'CITY_DRAWER_CHEAPEST_TIME_TO_FLY_FAILED',
      OWNER: 'Potato',
      CONTEXT:
        'When the City Drawer cheapest time to fly component fails to load due to missing data for all months.',
    },
    CITY_DRAWER_CHEAPEST_TIME_TO_FLY_VIEWED: {
      NAME: 'CITY_DRAWER_CHEAPEST_TIME_TO_FLY_VIEWED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer cheapest time to fly component is viewed by the user.',
    },
    CITY_DRAWER_PLACES_TO_STAY_FAILED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_FAILED',
      OWNER: 'Potato',
      CONTEXT:
        'When the City Drawer places to stay component fails to load due to missing hotel data.',
    },
    CITY_DRAWER_PLACES_TO_STAY_LOADED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_LOADED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer places to stay component loads successfully with hotel data.',
    },
    CITY_DRAWER_PLACES_TO_STAY_VIEWED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_VIEWED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer places to stay component is viewed by the user.',
    },
    CITY_DRAWER_DISCLAIMER_VIEWED: {
      NAME: 'CITY_DRAWER_DISCLAIMER_VIEWED',
      OWNER: 'Potato',
      CONTEXT: 'When the City Drawer disclaimer component is viewed by the user.',
    },
    CITY_DRAWER_FLIGHTS_LINK_CLICKED: {
      NAME: 'CITY_DRAWER_FLIGHTS_LINK_CLICKED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller clicks the flights link in the City Drawer indicative pricing.',
    },
    CITY_DRAWER_HOTELS_LINK_CLICKED: {
      NAME: 'CITY_DRAWER_HOTELS_LINK_CLICKED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller clicks the hotels link in the City Drawer indicative pricing.',
    },
    CITY_DRAWER_CHEAPEST_TIME_TO_FLY_MONTH_CLICKED: {
      NAME: 'CITY_DRAWER_CHEAPEST_TIME_TO_FLY_MONTH_CLICKED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller clicks a month tile in the Cheapest Time to Fly section.',
    },
    CITY_DRAWER_CHEAPEST_TIME_TO_FLY_TOGGLE_CLICKED: {
      NAME: 'CITY_DRAWER_CHEAPEST_TIME_TO_FLY_TOGGLE_CLICKED',
      OWNER: 'Potato',
      CONTEXT:
        'When a traveller clicks the Show More or Show Fewer Months button in the Cheapest Time to Fly section. Includes direction parameter (more/less).',
    },
    CITY_DRAWER_PLACES_TO_STAY_BUTTON_CLICKED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_BUTTON_CLICKED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller clicks the Show All button in the Places to Stay section.',
    },
    CITY_DRAWER_PLACES_TO_STAY_CARD_CLICKED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_CARD_CLICKED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller clicks a hotel card in the Places to Stay section.',
    },
    CITY_DRAWER_IMAGE_CAROUSEL_NAVIGATED: {
      NAME: 'CITY_DRAWER_IMAGE_CAROUSEL_NAVIGATED',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller navigates the image carousel using swipe or arrow buttons.',
    },
    CITY_DRAWER_PLACES_TO_STAY_NAVIGATED: {
      NAME: 'CITY_DRAWER_PLACES_TO_STAY_NAVIGATED',
      OWNER: 'Potato',
      CONTEXT:
        'When a traveller navigates the Places to Stay carousel using swipe or arrow buttons.',
    },

    INTERACTION_TEST_EVENT: {
      NAME: 'INTERACTION_TEST_EVENT',
      OWNER: 'Shuttle/Woodpecker',
      CONTEXT: 'Used in test files as mock data',
    },
    GLOBAL_HEADER_LOGO_CLICKED: {
      NAME: 'Global_Header_Logo_Clicked',
      OWNER: 'Shuttle/Woodpecker',
    },
    GLOBAL_HEADER_CULTURE_SELECTOR_CLICKED: {
      NAME: 'Global_Header_Culture_Selector_Clicked',
      OWNER: 'Shuttle/Woodpecker',
    },
    GLOBAL_HEADER_LOGIN_CLICKED: {
      NAME: 'Global_Header_LogIn_Clicked',
      OWNER: 'Shuttle/Woodpecker',
    },
    GLOBAL_HEADER_HEART_ICON_CLICKED: {
      NAME: 'Global_Header_Heart_Icon_Clicked',
      OWNER: 'Shuttle/Woodpecker',
    },
    GLOBAL_HEADER_PROFILE_CLICKED: {
      NAME: 'Global_Header_Profile_Clicked',
      OWNER: 'Shuttle/Woodpecker',
    },
    HOMEPAGE_TABS_LOADED: {
      NAME: 'Homepage_Tabs_Loaded',
      OWNER: 'Shuttle/Woodpecker',
    },
    HOMEPAGE_TABS_TAPPED: {
      NAME: 'Homepage_Tabs_Tapped',
      OWNER: 'Shuttle/Woodpecker',
    },

    CULTURE_SELECTOR_TAPPED: {
      NAME: 'Culture_Selector_Tapped',
      OWNER: 'BabelFish',
    },

    ...SEARCH_CONTROLS_EVENTS_BY_CATEGORY.INTERACTION,
    ...ALTERNATIVE_DATES_EVENTS_BY_CATEGORY.INTERACTION,

    CAR_HIRE_CROSS_SELL_FALLBACK_LOADED: {
      NAME: 'Car_Hire_Cross_Sell_Fallback_Loaded',
      OWNER: 'Capybara',
    },
    CAR_HIRE_CROSS_SELL_TAPPED: {
      NAME: 'Car_Hire_Cross_Sell_Tapped',
      OWNER: 'Capybara',
    },
    CAR_HIRE_PILL_TAPPED: {
      NAME: 'Car_Hire_Pill_Tapped',
      OWNER: 'Capybara',
    },
    HOTELS_CROSS_SELL_FALLBACK_LOADED: {
      NAME: 'Hotels_Cross_Sell_Fallback_Loaded',
      OWNER: 'Capybara',
    },
    HOTELS_CROSS_SELL_TAPPED: {
      NAME: 'Hotels_Cross_Sell_Tapped',
      OWNER: 'Capybara',
    },

    BOOKING_CONFIDENCE_MESSAGE_DISPLAYED: {
      NAME: 'Booking_Confidence_Message_Displayed',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_DISMISSED_X: {
      NAME: 'Booking_Confidence_Message_Dismissed_X',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_DISMISSED_CONTINUE: {
      NAME: 'Booking_Confidence_Message_Dismissed_Continue',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_DISMISSED_CLOSED: {
      NAME: 'Booking_Confidence_Message_Dismissed_Closed',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        'When the close button, continue button, or grey background of the confidence message panel is clicked',
    },
    ACORN_BOOKING_PANEL_URL_DETECTED: {
      NAME: 'Acorn_Booking_Panel_Url_Detected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When combined results/booking panel is loaded from an Acorn URL',
    },
    CODESHARE_OTA_DISPLAYED: {
      NAME: 'Codeshare_OTA_Displayed',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When codeshare information is displayed to the user on the booking panel',
    },
    CODESHARE_OTA_SELECTED: {
      NAME: 'Codeshare_OTA_Selected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a codeshare OTA is selected/clicked on the booking panel',
    },
    DEAL_MESSAGES_RENDERED: {
      NAME: 'Deal_Messages_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'On the Booking Panel',
    },
    DEAL_MESSAGE_CLICKED: {
      NAME: 'Deal_Message_Clicked',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'On the Booking Panel',
    },
    FLIGHTS_ADVERTS_REMOVED_DUE_TO_FILTERS: {
      NAME: 'Flights_Adverts_Removed_Due_To_Filters',
      OWNER: 'Graviton',
    },
    FLIGHT_BOOKING_PANEL_CLOSED: {
      NAME: 'Flight_Booking_Panel_Closed',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHT_BOOKING_PANEL_UNAVAILABLE_ITINERARY_RENDERED: {
      NAME: 'Flight_Booking_Panel_Unavailable_Itinerary_Rendered',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHTS_BOOKING_PANEL_FETCH: {
      NAME: 'Flights_Booking_Panel_Fetch',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When call /booking endpoint',
    },
    FLIGHTS_BOOKING_PANEL_LANDING: {
      NAME: 'Flights_Booking_Panel_Landing',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When the Booking Panel opens',
    },
    FLIGHT_BOOKING_PANEL_FLOATING_SUCCESS_NOTIFICATION_CTA_CLICKED: {
      NAME: 'Flight_Booking_Panel_Floating_Success_Notification_CTA_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHT_BOOKING_PANEL_ITINERARY_HEART_ICON_CLICKED: {
      NAME: 'Flight_Booking_Panel_Itinerary_Heart_Icon_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    MASHUP_OPTION_SELECTED: {
      NAME: 'Mashup_Option_Selected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When user clicks on a mashup option on the mashup screen',
    },
    MASHUP_SCREEN_DISPLAYED: {
      NAME: 'Mashup_Screen_Displayed',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When mashup / multi-book screen is displayed on the booking panel',
    },
    MASHUP_PANEL_CLOSED: {
      NAME: 'Mashup_Panel_Closed',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When mashup / multi-book screen is closed',
    },
    PSS_OPENED_FROM_MASHUP_OPTION: {
      NAME: 'Pss_Opened_From_Mashup_Option',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When PSS screen is opened from a mashup option on flights results',
    },
    ITINERARY_LEG_CLICKED: {
      NAME: 'Itinerary_Leg_Clicked',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a user expands the itinerary card and clicks on the itinerary leg.',
    },
    ITINERARY_DETAILS_DISPLAYED: {
      NAME: 'Itinerary_Details_Displayed',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a user expands the itinerary card and itinerary details are displayed.',
    },
    ITINERARY_DETAILS_CLOSE_CLICKED: {
      NAME: 'Itinerary_Details_Close_Clicked',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a user expands the itinerary card and clicks on the close button.',
    },
    ITINERARY_WITH_CHEAPEST_AIRLINE_SELECTED: {
      NAME: 'Itinerary_With_Cheapest_Airline_Selected',
      OWNER: 'Theseus',
      CONTEXT:
        'Traveller selects an itinerary card while the airline price label is visible and airline is the cheapest option',
    },
    ITINERARY_WITH_CHEAPEST_OTA_SELECTED: {
      NAME: 'Itinerary_With_Cheapest_Ota_Selected',
      OWNER: 'Theseus',
      CONTEXT:
        'Traveller selects an itinerary card while the airline price label is visible and an OTA is the cheapest option',
    },
    PRICING_ITEM_CLICKED: {
      NAME: 'Pricing_Item_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    READ_BEFORE_BOOKING_CLOSE_CLICKED: {
      NAME: 'Read_Before_Booking_Close_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    READ_BEFORE_BOOKING_DISPLAYED: {
      NAME: 'Read_Before_Booking_Displayed',
      OWNER: 'IrnBru/Theseus',
    },
    SELF_TRANSFER_BANNER_CLICKED: {
      NAME: 'Self_Transfer_Banner_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    SELF_TRANSFER_BANNER_CLOSED: {
      NAME: 'Self_Transfer_Banner_Closed',
      OWNER: 'IrnBru/Theseus',
    },
    SELF_TRANSFER_BANNER_DISPLAYED: {
      NAME: 'Self_Transfer_Banner_Displayed',
      OWNER: 'IrnBru/Theseus',
    },
    SELF_TRANSFER_BANNER_RENDERED: {
      NAME: 'Self_Transfer_Banner_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When self transfer banner is first rendered on the booking panel',
    },
    PQS_SURVEY_SEEN: {
      NAME: 'PQS_Survey_Seen',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        'When the browser tab with a PQS survey is visible (e.g. goes from a background to a foreground tab)',
    },
    PQS_SURVEY_RATED: {
      NAME: 'PQS_Survey_Rated',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a traveller selects a star rating in the PQS survey',
    },
    PQS_SURVEY_SUBMITTED: {
      NAME: 'PQS_Survey_Submitted',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a traveller submits feedback in the PQS survey',
    },
    PQS_CLICKED: {
      NAME: 'PQS_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_CLOSE_CLICKED: {
      NAME: 'PQS_Close_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PROVIDER_SELECTION_PQS_FIND_OUT_MORE_CLICKED: {
      NAME: 'Provider_Selection_PQS_Find_Out_More_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PASSENGER_INFO_CLICKED: {
      NAME: 'Passenger_Info_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PARTNER_MESSAGE_CLICKED: {
      NAME: 'Partner_Message_Clicked',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When partner messages is clicked on the Booking Panel',
    },
    PARTNER_MESSAGES_RENDERED: {
      NAME: 'Partner_Messages_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When partner messages are rendered on the Booking Panel',
    },
    PARTNER_TRUST_BADGES_RENDERED: {
      NAME: 'Partners_With_Trust_Badge_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When partner trust information is rendered on the Booking Panel',
    },
    PARTNER_WITH_TRUST_BADGE_SELECTED: {
      NAME: 'Partner_With_Trust_Badge_Selected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a trusted partner is selected on the Booking Panel',
    },
    BOOKING_PANEL_TIME_TO_FIRST_RESULT: {
      NAME: 'Booking_Panel_Time_To_First_Result',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_TIME_TO_LAST_RESULT: {
      NAME: 'Booking_Panel_Time_To_Last_Result',
      OWNER: 'IrnBru/Theseus',
    },
    CHECKING_PRICES_SPINNER_ACTIVE_DURATION: {
      NAME: 'Checking_Prices_Spinner_Active_Duration',
      OWNER: 'IrnBru/Theseus',
    },
    COMBINED_EXPLORE_FILTERS_LOADED: {
      NAME: 'Combined_Explore_Filters_Loaded',
      OWNER: 'Interstellar',
    },
    COMBINED_EXPLORE_RESULT_LOADED: {
      NAME: 'Combined_Explore_Result_Loaded',
      OWNER: 'Interstellar',
    },
    COMBINED_EXPLORE_CITY_COUNTRY_SWITCH_RESULTS_LOADED: {
      NAME: 'Combined_Explore_City_Country_Switch_Results_Loaded',
      OWNER: 'Interstellar',
    },
    COMBINED_EXPLORE_AIRLINE_SPONSORED_RESULT_SHOWN: {
      NAME: 'Combined_Explore_Airline_Sponsored_Result_Shown',
      OWNER: 'Quark',
    },
    COMBINED_EXPLORE_VIDEO_COVERAGE: {
      NAME: 'Combined_Explore_Video_Coverage',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_CLICKED: {
      NAME: 'Combined_Explore_Video_Clicked',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_CLOSE: {
      NAME: 'Combined_Explore_Video_Close',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_MILESTONE: {
      NAME: 'Combined_Explore_Video_Milestone',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_NAVIGATION: {
      NAME: 'Combined_Explore_Video_Navigation',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_CTA_CLICKED: {
      NAME: 'Combined_Explore_Video_Cta_Clicked',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_VIDEO_CTA_VIEWED: {
      NAME: 'Combined_Explore_Video_Cta_Viewed',
      OWNER: 'Potato',
    },
    COMBINED_EXPLORE_DEAL_BADGE_SEEN: {
      NAME: 'Combined_Explore_Deal_Badge_Seen',
      OWNER: 'Interstellar',
    },
    COMBINED_EXPLORE_CITY_COUNTRY_SWITCH_CLICKED: {
      NAME: 'Combined_Explore_City_Country_Switch_Clicked',
      OWNER: 'Interstellar',
    },
    EVENTS_RESULTS_LOADED: {
      NAME: 'Events_Results_Loaded',
      OWNER: 'Artemis',
      CONTEXT: 'When the events results finish loading successfully',
    },
    EVENTS_NO_RESULTS_LOADED: {
      NAME: 'Events_No_Results_Loaded',
      OWNER: 'Artemis',
      CONTEXT: 'When the events network request succeeds but returns zero events',
    },
    EVENTS_REQUEST_FAILED: {
      NAME: 'Events_Request_Failed',
      OWNER: 'Artemis',
      CONTEXT: 'When the events network request fails or returns an error response',
    },
    EVENTS_TAB_CHANGED: {
      NAME: 'Events_Tab_Changed',
      OWNER: 'Artemis',
      CONTEXT: 'When user switches between Destinations and Search by events tabs',
    },
    EVENTS_TAB_FIRST_CLICKED: {
      NAME: 'Events_Tab_First_Clicked',
      OWNER: 'Artemis',
      CONTEXT:
        'First time a user clicks the Search by events tab in a session — used to measure organic entry',
    },
    EVENTS_CATEGORY_FILTER_SELECTED: {
      NAME: 'Events_Category_Filter_Selected',
      OWNER: 'Artemis',
      CONTEXT: 'When user selects a category filter chip',
    },
    EVENTS_CARD_EXPANDED: {
      NAME: 'Events_Card_Expanded',
      OWNER: 'Artemis',
      CONTEXT: 'When user expands a parent event card to reveal child events',
    },
    EVENTS_CARD_FLIGHT_CLICKED: {
      NAME: 'Events_Card_Flight_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks a leaf event card to find flights',
    },
    EVENTS_CARD_CAR_HIRE_CLICKED: {
      NAME: 'Events_Card_Car_Hire_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks a leaf event card to find car hire',
    },
    EVENTS_CARD_HOTEL_CLICKED: {
      NAME: 'Events_Card_Hotel_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks a leaf event card to find hotels',
    },
    EVENTS_CHILD_ROW_CAR_HIRE_CLICKED: {
      NAME: 'Events_Child_Row_Car_Hire_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks Find car hire in an expanded child event row',
    },
    EVENTS_CHILD_ROW_FLIGHT_CLICKED: {
      NAME: 'Events_Child_Row_Flight_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks Find flights in an expanded child event row',
    },
    EVENTS_CHILD_ROW_HOTEL_CLICKED: {
      NAME: 'Events_Child_Row_Hotel_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks Find hotels in an expanded child event row',
    },
    EVENTS_MAP_OPENED: {
      NAME: 'Events_Map_Opened',
      OWNER: 'Artemis',
      CONTEXT: 'When user opens the map on mobile',
    },
    EVENTS_VIEWPORT_FILTER_APPLIED: {
      NAME: 'Events_Viewport_Filter_Applied',
      OWNER: 'Artemis',
      CONTEXT:
        'When the map viewport filter produces a non-empty result set (map <-> list sync is actively filtering). Fires for each distinct resultCount, and category on desktop, while the syncEventsMapAndList flag is on.',
    },
    EVENTS_NEARBY_FALLBACK_SHOWN: {
      NAME: 'Events_Nearby_Fallback_Shown',
      OWNER: 'Artemis',
      CONTEXT:
        'When the map viewport filter returns zero results and the nearby fallback activates. On desktop this renders a prompt with a "Show nearest results" CTA; on mobile the map auto-refocuses to the nearest events. Fires once per transition to the empty-viewport state while the syncEventsMapAndList flag is on.',
    },
    EVENTS_SHOW_NEAREST_RESULTS_CLICKED: {
      NAME: 'Events_Show_Nearest_Results_Clicked',
      OWNER: 'Artemis',
      CONTEXT:
        'When user clicks the nearby fallback CTA to zoom the map to the nearest events. Fires only when the syncEventsMapAndList flag is on.',
    },
    EVENTS_AIRPORT_MARKER_CLICKED: {
      NAME: 'Events_Airport_Marker_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks an airport price marker on the events map',
    },
    EVENTS_EXPERIENCE_MARKER_CLICKED: {
      NAME: 'Events_Experience_Marker_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks an event category marker on the events map',
    },
    EVENTS_EXPERIENCE_MARKER_POPOVER_CAR_HIRE_CLICKED: {
      NAME: 'Events_Experience_Marker_Popover_Car_Hire_Clicked',
      OWNER: 'Artemis',
      CONTEXT:
        'When user clicks the popover/tooltip of an event marker on the events map to find car hire',
    },
    EVENTS_EXPERIENCE_MARKER_POPOVER_CLICKED: {
      NAME: 'Events_Experience_Marker_Popover_Clicked',
      OWNER: 'Artemis',
      CONTEXT:
        'When user clicks the popover/tooltip of an event marker on the events map to find flights',
    },
    EVENTS_EXPERIENCE_MARKER_POPOVER_HOTEL_CLICKED: {
      NAME: 'Events_Experience_Marker_Popover_Hotel_Clicked',
      OWNER: 'Artemis',
      CONTEXT:
        'When user clicks the popover/tooltip of an event marker on the events map to find hotels',
    },
    EVENTS_RAIL_CARD_CAR_HIRE_CLICKED: {
      NAME: 'Events_Rail_Card_Car_Hire_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks an event card in the map modal rail to find car hire',
    },
    EVENTS_RAIL_CARD_FLIGHT_CLICKED: {
      NAME: 'Events_Rail_Card_Flight_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks an event card in the map modal rail to find flights',
    },
    EVENTS_RAIL_CARD_HOTEL_CLICKED: {
      NAME: 'Events_Rail_Card_Hotel_Clicked',
      OWNER: 'Artemis',
      CONTEXT: 'When user clicks an event card in the map modal rail to find hotels',
    },
    FLIGHT_BOOKING_PANEL_ITINERARY_AVAILABILITY_ESTABLISHED: {
      NAME: 'Flight_Booking_Panel_Itinerary_Availability_Established',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHT_BOOKING_PANEL_ITINERARY_AVAILABILITY_REVOKED: {
      NAME: 'Flight_Booking_Panel_Itinerary_Availability_Revoked',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHT_BOOKING_PANEL_GENERIC_ERROR_RENDERED: {
      NAME: 'Flight_Booking_Panel_Generic_Error_Rendered',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHT_BOOKING_PANEL_ERROR_DIALOG_BUTTON_CLICKED: {
      NAME: 'Flight_Booking_Panel_Error_Dialog_Button_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHTS_BOOKING_PANEL_ITINERARY_TOKEN_MIGRATION: {
      NAME: 'Flights_Booking_Panel_Itinerary_Token_Migration',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHTS_BOOKING_PANEL_USING_ITINERARY_TOKEN: {
      NAME: 'Flights_Booking_Panel_Using_Itinerary_Token',
      OWNER: 'IrnBru/Theseus',
    },
    FLIGHTS_SEARCH_ITINERARY_TOKEN_BOOKING_PANEL_USED: {
      NAME: 'Flights_Search_Itinerary_Token_Booking_Panel_Used',
      OWNER: 'IrnBru/Theseus',
    },
    PRICING_OPTION_SPINNERS_ACTIVE_DURATION: {
      NAME: 'Pricing_Option_Spinners_Active_Duration',
      OWNER: 'IrnBru/Theseus',
    },

    // Save to List (S2L) Booking Panel Call to Action Experiment - https://skyscanner.atlassian.net/browse/VEGA-563
    S2L_BOOKING_PANEL_CTA_SUBMIT_CLICKED: {
      NAME: 'S2L_Booking_Panel_CTA_Submit_Clicked',
      OWNER: 'IrnBru/Theseus',
    },
    S2L_BOOKING_PANEL_CTA_DISMISS_CLICKED: {
      NAME: 'S2L_Booking_Panel_CTA_Dismiss_Clicked',
      OWNER: 'IrnBru/Theseus',
    },

    // Payment Discounts POC Events
    PAYMENT_DISCOUNTS_RENDERED: {
      NAME: 'Payment_Discounts_Rendered',
      OWNER: 'Theseus',
    },
    PAYMENT_DISCOUNTS_CONTEXT_CLICKED: {
      NAME: 'Payment_Discounts_Context_Clicked',
      OWNER: 'Theseus',
    },
    PARTNER_WITH_PAYMENT_DISCOUNTS_SELECTED: {
      NAME: 'Partner_With_Payment_Discounts_Selected',
      OWNER: 'Theseus',
    },
    PAYMENT_DISCOUNTS_DETAILS_REDIRECT_TO_PARTNER: {
      NAME: 'Payment_Discounts_Details_Redirect_To_Partner',
      OWNER: 'Theseus',
    },
    CREDIT_CARD_FILTER_OPENED: {
      NAME: 'Credit_Card_Filter_Opened',
      OWNER: 'Theseus',
    },
    CREDIT_CARD_FILTER_APPLIED: {
      NAME: 'Credit_Card_Filter_Applied',
      OWNER: 'Theseus',
    },

    // KakaoPay Events
    PARTNER_WITH_KAKAO_CASHBACK_RENDERED: {
      NAME: 'Partner_With_Kakao_Cashback_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When the KakaoPay cashback message is visible on the Booking Panel',
    },
    PARTNER_WITH_KAKAO_CASHBACK_SELECTED: {
      NAME: 'Partner_With_Kakao_Cashback_Selected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When a KakaoPay-eligible pricing option is selected on the Booking Panel',
    },

    // Advert interactions
    HOMEPAGE_VIDEO_AD_CARD_TAPPED: {
      NAME: 'Homepage_Video_Ad_Card_Tapped',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_COMPLETED: {
      NAME: 'Homepage_Video_Ad_Completed',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_CTA_TAPPED: {
      NAME: 'Homepage_Video_Ad_Cta_Tapped',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_FULLSCREEN_VIEWED: {
      NAME: 'Homepage_Video_Ad_Fullscreen_Viewed',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_LOADED: {
      NAME: 'Homepage_Video_Ad_Loaded',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_MILESTONE: {
      NAME: 'Homepage_Video_Ad_Milestone',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_STOPPED: {
      NAME: 'Homepage_Video_Ad_Stopped',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_VIEWED: {
      NAME: 'Homepage_Video_Ad_Viewed',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_PLAYED: {
      NAME: 'Homepage_Video_Ad_Played',
      OWNER: 'Quark',
    },
    HOMEPAGE_VIDEO_AD_FALLBACK_SHOWN: {
      NAME: 'Homepage_Video_Ad_Fallback_Shown',
      OWNER: 'Quark',
    },
    SPONSORED_PRICING_OPTION_CLICK: {
      NAME: 'Sponsored_Pricing_Option_Click',
      OWNER: 'Quark',
    },
    SPONSORED_PRICING_OPTION_IMPRESSION: {
      NAME: 'Sponsored_Pricing_Option_Impression',
      OWNER: 'Quark',
    },
    SPONSORED_PRICING_OPTION_DELIVERY: {
      NAME: 'Sponsored_Pricing_Option_Delivery',
      OWNER: 'Quark',
    },
    SPONSORED_PRICING_OPTION_NO_MATCH: {
      NAME: 'Sponsored_Pricing_Option_No_Match',
      OWNER: 'Quark',
    },
    RTS_HERO_IMPRESSION: {
      NAME: 'RTS_Hero_Impression',
      OWNER: 'Hermes',
    },
    RTS_HERO_INTERACTION: {
      NAME: 'RTS_Hero_Interaction',
      OWNER: 'Hermes',
    },
    RTS_HOTEL_NAME_CLICKED: {
      NAME: 'RTS_Hotel_Name_Clicked',
      OWNER: 'Hermes',
    },
    RTS_GO_TO_HOTEL_CLICKED: {
      NAME: 'RTS_Go_To_Hotel_Clicked',
      OWNER: 'Hermes',
    },
    RTS_ALTERNATIVE_IMPRESSION: {
      NAME: 'RTS_Alternative_Impression',
      OWNER: 'Hermes',
    },
    RTS_ALTERNATIVE_INTERACTION: {
      NAME: 'RTS_Alternative_Interaction',
      OWNER: 'Hermes',
    },
    RTS_RESPONSE: {
      NAME: 'RTS_Response',
      OWNER: 'Hermes',
    },
    RTS_EXPLORE_MORE_CLICKED: {
      NAME: 'RTS_Explore_More_Clicked',
      OWNER: 'Hermes',
    },
    SPONSORED_DESTINATION_V2_DRAWER_OPENED: {
      NAME: 'Sponsored_Destination_V2_Drawer_Opened',
      OWNER: 'Quark',
    },
    SPONSORED_DESTINATION_V2_DRAWER_CLOSED: {
      NAME: 'Sponsored_Destination_V2_Drawer_Closed',
      OWNER: 'Quark',
    },
    SPONSORED_DESTINATION_V2_BANNER_VIEW: {
      NAME: 'Sponsored_Destination_V2_Banner_View',
      OWNER: 'Quark',
    },
    SPONSORED_DESTINATION_V2_FULL_DRAWER_VIEW: {
      NAME: 'Sponsored_Destination_V2_Full_Drawer_View',
      OWNER: 'Quark',
    },
    SPONSORED_DESTINATION_V2_AD_INFO_CLICKED: {
      NAME: 'Sponsored_Destination_V2_Ad_Info_Clicked',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_FILTER_ENABLED: {
      NAME: 'Sponsored_Airlines_V2_Filter_Enabled',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_FILTER_DISABLED: {
      NAME: 'Sponsored_Airlines_V2_Filter_Disabled',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_BANNER_VIEW: {
      NAME: 'Sponsored_Airlines_V2_Banner_View',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_PARTNER_INVENTORY_CHECK: {
      NAME: 'Sponsored_Airlines_V2_Partner_Inventory_Check',
      OWNER: 'Quark',
    },
    SPONSORED_DSA_LEGAL_NAME_DIVERGENCE: {
      NAME: 'Sponsored_DSA_Legal_Name_Divergence',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DRAWER_OPENED: {
      NAME: 'Sponsored_Airlines_V2_Drawer_Opened',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DRAWER_CLOSED: {
      NAME: 'Sponsored_Airlines_V2_Drawer_Closed',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DIRECT_REDIRECT_AFTER_FILTER: {
      NAME: 'Sponsored_Airlines_V2_Direct_Redirect_After_Filter',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_BOOKING_PANEL_REDIRECT_AFTER_FILTER: {
      NAME: 'Sponsored_Airlines_V2_Booking_Panel_Redirect_After_Filter',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_REDIRECT_TO_BOOKING_PANEL: {
      NAME: 'Sponsored_Airlines_V2_Redirect_To_Booking_Panel',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_FULL_DRAWER_VIEW: {
      NAME: 'Sponsored_Airlines_V2_Full_Drawer_View',
      OWNER: 'Quark',
    },

    EXPLORE_BRAND_AIRLINES_FILTER_ENABLED: {
      NAME: 'Explore_Brand_Airlines_Filter_Enabled',
      OWNER: 'Quark',
    },
    EXPLORE_BRAND_AIRLINES_FILTER_DISABLED: {
      NAME: 'Explore_Brand_Airlines_Filter_Disabled',
      OWNER: 'Quark',
    },
    EXPLORE_BRAND_AIRLINES_REDIRECT_TO_DAY_VIEW: {
      NAME: 'Explore_Brand_Airlines_Redirect_To_Day_View',
      OWNER: 'Quark',
    },

    FLIGHTS_PRICE_BANDS_RENDERED: {
      NAME: 'Flights_Price_Bands_Rendered',
      OWNER: 'Luna',
    },
    FLIGHTS_FILTERS_ONLY_SELECTED: {
      NAME: 'Select_Only_One_Carrier_Filter',
      OWNER: 'Luna',
      CONTEXT:
        'When a user selects a filter in the filters only view (e.g. from the sidebar on desktop and drawer on mweb).',
    },
    FLIGHT_DAY_VIEW_FILTER_OPENED: {
      NAME: 'Flight_Day_View_Filter_Opened',
      OWNER: 'Luna',
      CONTEXT: 'When the filter page loads, not full rendering.',
    },
    FLIGHT_DAY_VIEW_SORT_OPENED: {
      NAME: 'Flight_Day_View_Sort_Opened',
      OWNER: 'Luna',
      CONTEXT: 'When the sort filter screen is shown',
    },
    FLIGHTS_FILTER_INTERACTION_TRIGGERED: {
      NAME: 'Flights_Filter_Interaction_Triggered',
      OWNER: 'Luna',
      CONTEXT: 'When a traveller interacts with flights filters in the Filters Drawer.',
    },
    SHOW_MORE_FLIGHTS_TAPPED: {
      NAME: 'Show_More_Flights_Tapped',
      OWNER: 'Luna',
      CONTEXT: 'When a traveller taps on the show more flights button on combined results.',
    },
    COMBINED_RESULTS_FLOATING_SUCCESS_NOTIFICATION_CTA_CLICKED: {
      NAME: 'Combined_Results_Floating_Success_Notification_CTA_Clicked',
      OWNER: 'Luna',
      CONTEXT: 'When the CTA of the floating success notification is clicked',
    },
    COMBINED_RESULTS_ITINERARY_HEART_ICON_CLICKED: {
      NAME: 'Combined_Results_Itinerary_Heart_Icon_Clicked',
      OWNER: 'Luna',
      CONTEXT: 'When the heart icon on a flight card (itinerary) is pressed',
    },
    NO_FLIGHTS_LOADED: {
      NAME: 'No_Flights_Loaded',
      OWNER: 'Luna',
    },
    FLIGHTS_FILTER_TCS_BAGGAGE_NEW_SEARCH: {
      NAME: 'Flights_Filter_TCS_Baggage_New_Search',
      OWNER: 'Luna',
    },
    COMBINED_RESULTS_TIME_TO_FIRST_RESULT: {
      NAME: 'Combined_Results_Time_To_First_Result',
      OWNER: 'Luna',
    },
    COMBINED_RESULTS_TIME_TO_LAST_RESULT: {
      NAME: 'Combined_Results_Time_To_Last_Result',
      OWNER: 'Luna',
    },

    // Packages
    PACKAGES_BANNER_CLICKED: {
      NAME: 'Packages_Banner_Clicked',
      OWNER: 'Hermes',
    },
    PACKAGES_BANNER_RENDERED: {
      NAME: 'Packages_Banner_Rendered',
      OWNER: 'Hermes',
    },
    PRICE_ALERT_SIGNUP_CLICKED: {
      NAME: 'Price_Alert_Signup_Clicked',
      OWNER: 'Sentinel',
    },
    PRICE_ALERT_SIGNUP_USER_LOGGED_IN: {
      NAME: 'Price_Alert_Signup_User_Logged_In',
      OWNER: 'Sentinel',
    },
    PRICE_ALERT_SUCCESS_MODAL_SHOWN: {
      NAME: 'Price_Alert_Success_Modal_Shown',
      OWNER: 'Sentinel',
    },

    // Loyalty Discounts POC Events
    LOYALTY_DISCOUNTS_NO_DEAL_LINK_CLICKED: {
      NAME: 'Loyalty_Discounts_No_Deal_Link_Clicked',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when a user clicks on the no deal link on the Pricing Item.',
    },
    LOYALTY_DISCOUNTS_LOGIN_CLICKED: {
      NAME: 'Loyalty_Discounts_Login_Clicked',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when a user clicks on the loyalty login button on the Pricing Item.',
    },
    LOYALTY_DISCOUNTS_DEAL_LINK_CLICKED: {
      NAME: 'Loyalty_Discounts_Deal_Link_Clicked',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when a user clicks on the deal link on the Pricing Item.',
    },
    LOYALTY_DISCOUNTS_TOTAL_COSTS_DEAL_LINK_CLICKED: {
      NAME: 'Loyalty_Discounts_Total_Costs_Deal_Link_Clicked',
      OWNER: 'Greyfriars',
    },
    LOYALTY_DISCOUNTS_DEAL_BANNER_RENDERED: {
      NAME: 'Loyalty_Discounts_Deal_Banner_Rendered',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when a loyalty discount banner is rendered in the day view results.',
    },
    LOYALTY_DISCOUNTS_DEAL_DV_LOGIN_CLICKED: {
      NAME: 'Loyalty_Discounts_Deal_DV_Login_Clicked',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when a user clicks on the loyalty login button on a day-view itinerary.',
    },

    // Homepage Events
    GLOBAL_NAV_CARD_CLICKED: {
      NAME: 'Global_Nav_Card_Clicked',
      OWNER: 'Potato',
    },
    GLOBAL_NAV_CARD_LOADED: {
      NAME: 'Global_Nav_Card_Loaded',
      OWNER: 'Potato',
    },
    GLOBAL_NAV_FALLBACK_CARD_CLICKED: {
      NAME: 'Global_Nav_Fallback_Card_Clicked',
      OWNER: 'Potato',
    },
    GLOBAL_NAV_FALLBACK_CARD_LOADED: {
      NAME: 'Global_Nav_Fallback_Card_Loaded',
      OWNER: 'Potato',
    },
    HOMEPAGE_AD_LOADED: {
      NAME: 'Homepage_Ad_Loaded',
      OWNER: 'Potato',
      CONTEXT: 'When the ad is loaded on the Homepage.',
    },
    HOMEPAGE_AD_TAPPED: {
      NAME: 'Homepage_Ad_Tapped',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller taps the ad on the Homepage.',
    },
    HOMEPAGE_FALLBACK_AD_LOADED: {
      NAME: 'Homepage_Fallback_Ad_Loaded',
      OWNER: 'Potato',
      CONTEXT: 'When the fallback ad is loaded on the Homepage.',
    },
    HOMEPAGE_FALLBACK_AD_TAPPED: {
      NAME: 'Homepage_Fallback_Ad_Tapped',
      OWNER: 'Potato',
      CONTEXT: 'When a traveller taps the fallback ad on the Homepage.',
    },

    GLOBAL_NAV_RESPONSE_FAILURE: {
      NAME: 'Global_Nav_Response_Failure',
      OWNER: 'Potato',
      CONTEXT: 'When the global navigation response fails to load.',
    },

    CE_PILL_SELECTED: {
      NAME: 'CE_Pill_Selected',
      OWNER: 'Interstellar',
    },
    CE_RESULT_SELECTED: {
      NAME: 'CE_Result_Selected',
      OWNER: 'Interstellar',
    },
    CE_RESULT_VIEWED: {
      NAME: 'CE_Result_Viewed',
      OWNER: 'Interstellar',
    },
    CE_CHANGE_SEARCH_PARAMS: {
      NAME: 'CE_Change_Search_Params',
      OWNER: 'Interstellar',
    },
    CE_EXPLORE_EVERYWHERE: {
      NAME: 'CE_Explore_Everywhere',
      OWNER: 'Interstellar',
    },
    CE_ALL_FILTERS_CLICKED: {
      NAME: 'CE_All_Filters_Clicked',
      OWNER: 'Interstellar',
    },
    CE_FILTER_MODAL_VIEWED: {
      NAME: 'CE_Filter_Modal_Viewed',
      OWNER: 'Interstellar',
    },
    CE_QUICK_FILTER_CLICKED: {
      NAME: 'CE_Quick_Filter_Clicked',
      OWNER: 'Interstellar',
    },
    CE_FILTER_CHANGE: {
      NAME: 'CE_Filter_Change',
      OWNER: 'Interstellar',
    },
    CE_FILTERS_CLEARED: {
      NAME: 'CE_Filters_Cleared',
      OWNER: 'Interstellar',
    },
    CE_FILTERS_NO_RESULTS: {
      NAME: 'CE_Filters_No_Results',
      OWNER: 'Interstellar',
    },
    CE_PREMIUM_RAIL_RENDERED: {
      NAME: 'ce_premium_rail_rendered',
      OWNER: 'IrnBru',
    },
    CE_PREMIUM_RAIL_RESULT_CLICKED: {
      NAME: 'ce_premium_rail_result_clicked',
      OWNER: 'IrnBru',
    },

    // World Cup Flights Finder Events
    WC_BANNER_RENDERED: {
      NAME: 'WC_Banner_Rendered',
      OWNER: 'Artemis',
    },
    WC_BANNER_CTA_CLICKED: {
      NAME: 'WC_Banner_CTA_Clicked',
      OWNER: 'Artemis',
    },
    WC_FLIGHT_LINK_CLICKED: {
      NAME: 'WC_Flight_Link_Clicked',
      OWNER: 'Artemis',
    },
    WC_CHANGE_TEAM_CLICKED: {
      NAME: 'WC_Change_Team_Clicked',
      OWNER: 'Artemis',
    },
    WC_TEAM_SELECTED: {
      NAME: 'WC_Team_Selected',
      OWNER: 'Artemis',
    },
    WC_HOTEL_LINK_CLICKED: {
      NAME: 'WC_Hotel_Link_Clicked',
      OWNER: 'Artemis',
    },
    WC_CAR_HIRE_LINK_CLICKED: {
      NAME: 'WC_Car_Hire_Link_Clicked',
      OWNER: 'Artemis',
    },

    // Miami Dolphins Flight Finder Events
    DOLPHINS_LOADED: {
      NAME: 'Dolphins_Loaded',
      OWNER: 'Artemis',
    },
    DOLPHINS_FLIGHT_CLICK: {
      NAME: 'Dolphins_Flight_Click',
      OWNER: 'Artemis',
    },
    DOLPHINS_HOTEL_CLICK: {
      NAME: 'Dolphins_Hotel_Click',
      OWNER: 'Artemis',
    },
    DOLPHINS_CAR_CLICK: {
      NAME: 'Dolphins_Car_Click',
      OWNER: 'Artemis',
    },
    DOLPHINS_SIDE_BANNER_CLICK: {
      NAME: 'Dolphins_Side_Banner_Click',
      OWNER: 'Artemis',
    },
    DOLPHINS_PLAYBOOK_BANNER_CLICK: {
      NAME: 'Dolphins_Playbook_Banner_Click',
      OWNER: 'Artemis',
    },

    ALTERNATE_DATES_FDV_RESULTS: {
      NAME: 'Alternate_Dates_FDV_Results',
      OWNER: 'Graviton',
      CONTEXT: 'Fired when polling completes on a Filtered Day View page',
    },

    // Single Leg Results Events
    RESULTS_PIN_STATE_CHANGED: {
      NAME: 'Results_Pin_State_Changed',
      OWNER: 'Goose',
      CONTEXT: 'Fires when a user pins or unpins a leg via the leg pin checkbox',
    },

    // Price Pulse Events
    PRICE_PULSE_ENABLED: {
      NAME: 'price_pulse_enabled',
      OWNER: 'Greyfriars',
      CONTEXT:
        'Fires once per session when the Price Pulse feature flag resolves true and the hook activates',
    },
    PRICE_PULSE_SHOWN: {
      NAME: 'price_pulse_shown',
      OWNER: 'Greyfriars',
      CONTEXT: 'First qualifying render of Price Pulse strikethrough per itinerary per session',
    },
    PRICE_PULSE_CLICKED: {
      NAME: 'price_pulse_clicked',
      OWNER: 'Greyfriars',
      CONTEXT: 'User clicks a card currently showing the Price Pulse strikethrough',
    },
    PRICE_PULSE_DISCLOSURE_VIEWED: {
      NAME: 'price_pulse_disclosure_viewed',
      OWNER: 'Greyfriars',
      CONTEXT: 'Info-icon popover opens on a Price Pulse card',
    },
  },

  AUTOSUGGEST: {
    AUTOSUGGEST_CONTROLS_ACTIVATED: {
      NAME: 'Autosuggest_Controls_Activated',
      OWNER: 'Compass',
      CONTEXT: 'When a traveller clicks on the flight origin/destination selector.',
    },
    AUTOSUGGEST_RESULT_SELECTED: {
      NAME: 'Autosuggest_Result_Selected',
      OWNER: 'Compass',
      CONTEXT: 'When a traveller clicks on an autosuggested location.',
    },
    AUTOSUGGEST_RESULTS_UPDATED: {
      NAME: 'Autosuggest_Results_Updated',
      OWNER: 'Compass',
      CONTEXT: 'When autosuggest results are reloaded from server.',
    },
    UNSET_TELEMETRY_EVENT_TYPE: {
      NAME: 'Unset_telemetry_event_type',
      OWNER: 'Compass',
    },
  },

  BEHAVIOURAL: {
    BOOKING_PANEL: {
      NAME: 'booking_panel',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_SAVE_TO_LIST_INTERACTION: {
      NAME: 'booking_panel_save_to_list_interaction',
      OWNER: 'IrnBru/Theseus',
    },
    ...SEARCH_CONTROLS_EVENTS_BY_CATEGORY.BEHAVIOURAL,
    ...ALTERNATIVE_DATES_EVENTS_BY_CATEGORY.BEHAVIOURAL,
  },

  CHOKE_POINTS: {
    EXPERIMENT_CHOKE_POINT: {
      NAME: 'Experiment_Choke_Point',
      OWNER: 'Shuttle/Woodpecker',
    },
    USERS_WHO_QUALIFY_FOR_UPGRADE_MESSAGING: {
      NAME: 'users_who_qualify_for_upgrade_messaging',
      OWNER: 'IrnBru',
      CONTEXT:
        'Emitted from useUpgradeMessaging when both gates pass: traveller is in the experiment (including control) AND the searched route is in the curated eligible-routes set',
    },
    EXPLORE_AI_MODAL_CLICKED: {
      NAME: 'users_who_clicked_explore_ai_modal',
      OWNER: 'chAI',
      CONTEXT: 'Fired when a traveller opens a destination-card modal in ExploreAI.',
    },
  },

  FUNNEL: {
    ACQUISITION: {
      NAME: 'acquisition',
      OWNER: 'Tuxedo',
      CONTEXT: 'Fired on app initialisation. Indicates a user has entered Banana.',
    },
    DAY_VIEW: {
      NAME: 'Day_View',
      OWNER: 'Luna',
      CONTEXT:
        'Historical badly named event that does not relate to Day View and instead indicates when the Combined Results screen has been loaded. Mapped in eventMapping.ts to funnel events.',
    },
    HOMEPAGE: {
      NAME: 'Homepage',
      OWNER: 'Potato',
      CONTEXT:
        'When the Homepage screen has been loaded. Mapped in eventMapping.ts to funnel events.',
    },
    COMBINED_EXPLORE: {
      NAME: 'Combined_Explore',
      OWNER: 'Interstellar',
      CONTEXT:
        'When the Combined Explore screen has been loaded. Mapped in eventMapping.ts to funnel events.',
    },
    FLIGHT_BOOKING_PANEL_FIRST_RENDERED: {
      NAME: 'Flight_Booking_Panel_First_Rendered',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        'When the Booking Panel route (/details/*) is navigated to from Combined Results Flights (/). Fired before waiting for itineraries to load. Mapped in eventMapping.ts to funnel events.',
    },
    FLIGHT_SEARCH_RESULTS_PAGE: {
      NAME: 'Flight_Search_Results_Page',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        'Filter/sort state of the search results page at the time the Booking Panel was opened. Fired separately from Flight_Booking_Panel_First_Rendered.',
    },
    FLIGHT_SEARCH_RESULTS_OPTION: {
      NAME: 'Flight_Search_Results_Option',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        'Itineraries visible in search results at the time the Booking Panel was opened. Fired separately from Flight_Booking_Panel_First_Rendered.',
    },
    TRACKING_PREFERENCES_CHANGED: {
      NAME: 'Tracking_Preferences_Changed',
      OWNER: 'Arcane',
      CONTEXT: 'Allows us to respect user tracking preferences when updated',
    },
    FLIGHT_ITINERARY_PRICING_OPTION: {
      NAME: 'Flight_Itinerary_Pricing_Option',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'When the Flight booking panel has mounted we send all flight pricing options',
    },
  },

  MINIEVENT_MISC: {
    // Do not add new MINIEVENT_MISC events, only added due to historical usages.
    // 'Minievent Misc' means the event constant is used in the minievent tracking util and has its own topic.
    AD_SPONSORED_CONTENT_WIDGET_ELEMENT_CLICK: {
      NAME: 'ad_sponsored_content_widget_element_click',
      OWNER: 'Graviton',
    },
    AD_SPONSORED_CONTENT_WIDGET_ELEMENT_ENGAGE: {
      NAME: 'ad_sponsored_content_widget_element_engage',
      OWNER: 'Graviton',
    },
    AD_SPONSORED_CONTENT_WIDGET_ENGAGE: {
      NAME: 'ad_sponsored_content_widget_engage',
      OWNER: 'Graviton',
    },
    AD_SPONSORED_CONTENT_WIDGET_LOAD: {
      NAME: 'ad_sponsored_content_widget_load',
      OWNER: 'Graviton',
    },
    BOOKING_PANEL_MULTIBOOK_PARTNER_SELECTED: {
      NAME: 'booking_panel_multibook_partner_selected',
      OWNER: 'IrnBru/Theseus',
    },
    ...SEARCH_CONTROLS_EVENTS_BY_CATEGORY.MINIEVENT_MISC,
    ...ALTERNATIVE_DATES_EVENTS_BY_CATEGORY.MINIEVENT_MISC,
    CONFIG_PAGE_LANDING: {
      NAME: 'config_page_landing',
      OWNER: 'IrnBru',
    },
    PQS_MINI_SURVEY: {
      NAME: 'pqs_mini_survey',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_FUNNEL_EVENTS: {
      NAME: 'funnel_events',
      OWNER: 'IrnBru/Theseus',
      CONTEXT: 'These events are extremely generic but are used in the PQS mini survey tracking',
    },
    BOOKING_PANEL_NAVIGATE_TO_SAVED_LIST: {
      NAME: 'booking_panel_navigate_to_saved_list',
      OWNER: 'IrnBru/Theseus',
    },
  },

  // Do not add new MIXED events, only added due to historical usages. 'Mixed' means the event constant is used across multiple tracking utils, for example both trackBehaviouralEvent and trackInteractionEvent.
  // Using an Analytics Event as an Operational Event name does not count as 'mixed' in this context, 'mixing' is only when using across different analytics schemas
  MIXED: {
    FLIGHT_DAY_VIEW_PARTNER_SELECTED: {
      NAME: 'Flight_Day_View_Partner_Selected',
      OWNER: 'IrnBru/Theseus',
      CONTEXT:
        '1) When a traveller selects a pricing option to redirect to or 2) When a traveller opens the multi book panel',
    },
    RAIL_DRAWER_OPENED: {
      NAME: 'Rail_Drawer_Opened',
      OWNER: 'Coral',
    },
    RAIL_DRAWER_CLOSED: {
      NAME: 'Rail_Drawer_Closed',
      OWNER: 'Coral',
    },
    RAIL_BANNER_LOADED: {
      NAME: 'Rail_Banner_Loaded',
      OWNER: 'Coral',
    },
    RAIL_BANNER_CLICKED: {
      NAME: 'Rail_Banner_Clicked',
      OWNER: 'Coral',
    },
    RAIL_ITINERARY_CLICKED: {
      NAME: 'Rail_Itinerary_Clicked',
      OWNER: 'Coral',
    },
    RAIL_SEE_MORE_CLICKED: {
      NAME: 'Rail_See_More_Clicked',
      OWNER: 'Coral',
    },
    SECONDARY_SORT_SELECTED: {
      NAME: 'Secondary_Sort_Selected',
      OWNER: 'Coral',
    },
    BOOKING_PANEL_BAGGAGE_BADGE: {
      NAME: 'booking_panel_baggage_badge',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_FILTER_BUTTON: {
      NAME: 'booking_panel_filter_button',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_UPSELL_BUTTON: {
      NAME: 'booking_panel_upsell_button',
      OWNER: 'IrnBru/Theseus',
    },
    BP_AGENT_FARES_LIST_GO_TO_SITE: {
      NAME: 'bp_agent_fares_list_go_to_site',
      OWNER: 'IrnBru/Theseus',
    },
    BP_AGENT_FARES_LIST: {
      NAME: 'bp_agent_fares_list',
      OWNER: 'IrnBru/Theseus',
    },
    MASHUP_FARE_AVAILABLE: {
      NAME: 'Mashup_Fare_Available',
      OWNER: 'IrnBru/Theseus',
    },
  },

  SMART_METRICS: {
    EXPERIMENT_SMART_METRIC: {
      NAME: 'Experiment_Smart_Metric',
      OWNER: 'Shuttle/Woodpecker',
    },
    LOYALTY_DISCOUNTS_RENDERED: {
      NAME: 'Loyalty_Discounts_Rendered',
      OWNER: 'Greyfriars',
      CONTEXT: 'Emitted when the Loyalty Discounts component is rendered on the Pricing Item.',
    },
    PRICE_BANDS_RENDERED_FLIGHTS_RESULTS: {
      NAME: 'price_bands_rendered_flights_results',
      OWNER: 'Luna',
      CONTEXT: 'smart metric emitted when Price Bands component is rendered',
    },
    COMBINED_RESULTS_FLIGHT_LINK_CLICKED: {
      NAME: 'CombinedResults_FlightLink_Clicked',
      OWNER: 'Luna',
    },
    COMBINED_RESULTS_HOTEL_LINK_CLICKED: {
      NAME: 'CombinedResults_HotelLink_Clicked',
      OWNER: 'Capybara',
    },
    CITY_DRAWER_ENGAGEMENT: {
      NAME: 'city_drawer_engagement',
      OWNER: 'Potato',
      CONTEXT: 'Emitted when a user actively engages with content inside the City Drawer',
    },
    USERS_WHO_PINNED_A_LEG: {
      NAME: 'users_who_pinned_a_leg',
      OWNER: 'Goose',
      CONTEXT: 'Emitted when a user pins a leg via the leg pin checkbox on single leg results',
    },
    EXPLORE_BRAND_AIRLINES_FILTER_ENABLED: {
      NAME: 'Explore_Brand_Airlines_Filter_Enabled',
      OWNER: 'Quark',
    },
    EXPLORE_BRAND_AIRLINES_FILTER_DISABLED: {
      NAME: 'Explore_Brand_Airlines_Filter_Disabled',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_FILTER_ENABLED: {
      NAME: 'Sponsored_Airlines_V2_Filter_Enabled',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_FILTER_DISABLED: {
      NAME: 'Sponsored_Airlines_V2_Filter_Disabled',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DRAWER_OPENED: {
      NAME: 'Sponsored_Airlines_V2_Drawer_Opened',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DRAWER_CLOSED: {
      NAME: 'Sponsored_Airlines_V2_Drawer_Closed',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_DIRECT_REDIRECT_AFTER_FILTER: {
      NAME: 'Sponsored_Airlines_V2_Direct_Redirect_After_Filter',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_BOOKING_PANEL_REDIRECT_AFTER_FILTER: {
      NAME: 'Sponsored_Airlines_V2_Booking_Panel_Redirect_After_Filter',
      OWNER: 'Quark',
    },
    SPONSORED_AIRLINES_V2_REDIRECT_TO_BOOKING_PANEL: {
      NAME: 'Sponsored_Airlines_V2_Redirect_To_Booking_Panel',
      OWNER: 'Quark',
    },
    UPGRADE_MESSAGING_CARD_CTA_CLICKED: {
      NAME: 'Upgrade_Messaging_Card_Cta_Clicked',
      OWNER: 'IrnBru',
      CONTEXT:
        'Emitted when a traveller clicks the CTA on the premium upgrade messaging card on search results',
    },
    CITY_DRAWER_REOPEN_LOW: {
      NAME: 'city_drawer_reopen_low',
      OWNER: 'Potato',
      CONTEXT:
        'Fired once when the City Drawer has been opened at least 2 times in a page session.',
    },
    CITY_DRAWER_REOPEN_MEDIUM: {
      NAME: 'city_drawer_reopen_medium',
      OWNER: 'Potato',
      CONTEXT:
        'Fired once when the City Drawer has been opened at least 4 times in a page session.',
    },
    CITY_DRAWER_REOPEN_HIGH: {
      NAME: 'city_drawer_reopen_high',
      OWNER: 'Potato',
      CONTEXT:
        'Fired once when the City Drawer has been opened at least 7 times in a page session.',
    },
    EXPLORE_SPONSORED_AIRLINE_BANNER_DISPLAYED: {
      NAME: 'Explore_Sponsored_Airline_Banner_Displayed',
      OWNER: 'Quark',
      CONTEXT:
        'Fired when a variant B user is served and sees the sponsored airline banner on Explore',
    },
    FLIGHTS_REDIRECTED_ONEWAY_TRIP: {
      NAME: 'flights_redirected_oneway_trip',
      OWNER: 'Goose',
      CONTEXT: 'Fired when a user redirects to a partner from a one-way search',
    },
    FLIGHTS_REDIRECTED_RETURN_TRIP: {
      NAME: 'flights_redirected_return_trip',
      OWNER: 'Goose',
      CONTEXT: 'Fired when a user redirects to a partner from a return search',
    },
    FLIGHTS_REDIRECTED_MULTICITY_TRIP: {
      NAME: 'flights_redirected_multicity_trip',
      OWNER: 'Goose',
      CONTEXT: 'Fired when a user redirects to a partner from a multi-city search',
    },
    USERS_CLICKED_PREMIUM_RAIL_RESULT_CE: {
      NAME: 'users_clicked_premium_rail_result_ce',
      OWNER: 'IrnBru',
    },
    CE_CITY_COUNTRY_SWITCH_CLICKED: {
      NAME: 'CE_City_Country_Switch_Clicked',
      OWNER: 'Interstellar',
    },
    EVENTS_CARD_FLIGHT_CLICKED: {
      NAME: 'events_card_flight_clicked',
      OWNER: 'Artemis',
      CONTEXT: 'Emitted when a user redirects to flights from any experience card surface',
    },
    EVENTS_CARD_CAR_HIRE_CLICKED: {
      NAME: 'events_card_car_hire_clicked',
      OWNER: 'Artemis',
      CONTEXT: 'Emitted when a user redirects to car hire from any experience card surface',
    },
    EVENTS_CARD_HOTEL_CLICKED: {
      NAME: 'events_card_hotel_clicked',
      OWNER: 'Artemis',
      CONTEXT: 'Emitted when a user redirects to hotels from any experience card surface',
    },
    USERS_WHO_INTERACTED_WITH_EVENTS_MAP: {
      NAME: 'users_who_interacted_with_events_map',
      OWNER: 'Artemis',
      CONTEXT:
        'Fires once per session on the first user-driven map bounds change (pan/zoom), regardless of the syncEventsMapAndList flag. Used to compare map engagement between flag-on and flag-off variants.',
    },
    EVENTS_CARD_CLICKED_AFTER_MAP_INTERACTION: {
      NAME: 'events_card_clicked_after_map_interaction',
      OWNER: 'Artemis',
      CONTEXT:
        'Fires once per session when a user redirects from an experience card (flight/hotel/car surface) after they have interacted with the events map (pan/zoom). Regardless of the syncEventsMapAndList flag. Used to measure whether map <-> list sync improves conversion in the experiment.',
    },
    CE_COUNTRY_PAGE_VIEWED: {
      NAME: 'CE_Country_Page_Viewed',
      OWNER: 'Interstellar',
    },
    CE_CARDS_VIEWED_6: {
      NAME: 'CE_Cards_Viewed_6',
      OWNER: 'Interstellar',
      CONTEXT:
        'Fired once when a user has scrolled to view at least 6 place cards in a page session on Combined Explore.',
    },
    CE_CARDS_VIEWED_12: {
      NAME: 'CE_Cards_Viewed_12',
      OWNER: 'Interstellar',
      CONTEXT:
        'Fired once when a user has scrolled to view at least 12 place cards in a page session on Combined Explore.',
    },
    CE_CARDS_VIEWED_24: {
      NAME: 'CE_Cards_Viewed_24',
      OWNER: 'Interstellar',
      CONTEXT:
        'Fired once when a user has scrolled to view at least 24 place cards in a page session on Combined Explore.',
    },
    CE_CARDS_VIEWED_48: {
      NAME: 'CE_Cards_Viewed_48',
      OWNER: 'Interstellar',
      CONTEXT:
        'Fired once when a user has scrolled to view at least 48 place cards in a page session on Combined Explore.',
    },
  },

  UI: {
    BOOKING_PANEL_LOADED: {
      NAME: 'booking_panel_loaded',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_COMPLETED: {
      NAME: 'booking_panel_completed',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_RENDERED: {
      NAME: 'booking_confidence_message_rendered',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_X_CLICKED: {
      NAME: 'booking_confidence_message_x_clicked',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_BACKGROUND_CLICKED: {
      NAME: 'booking_confidence_message_background_clicked',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_CLOSED: {
      NAME: 'booking_confidence_message_closed',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_CONFIDENCE_MESSAGE_DISMISSED: {
      NAME: 'booking_confidence_message_dismissed',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_SURVEY_SEEN: {
      NAME: 'PQS_Survey_Seen',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_SURVEY_DELAY_NOT_REACHED: {
      NAME: 'PQS_Survey_Delay_Not_Reached',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_SURVEY_DISMISSED: {
      NAME: 'PQS_Survey_Dismissed',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_SURVEY_SUBMITTED: {
      NAME: 'PQS_Survey_Submitted',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_SURVEY_STILL_SEARCHING_DISMISSED: {
      NAME: 'PQS_Survey_Still_Searching_Dismissed',
      OWNER: 'IrnBru/Theseus',
    },
    PROVIDER_SELECTION_PQS_FIND_OUT_MORE_CLICKED: {
      NAME: 'provider_selection_pqs_find_out_more_clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_CLICKED: {
      NAME: 'pqs_clicked',
      OWNER: 'IrnBru/Theseus',
    },
    PQS_CLOSE_CLICKED: {
      NAME: 'pqs_close_clicked',
      OWNER: 'IrnBru/Theseus',
    },
    BOOKING_PANEL_UNAVAILABLE_ITINERARY: {
      NAME: 'booking_panel_unavailable_itinerary',
      OWNER: 'IrnBru/Theseus',
    },
    ...SEARCH_CONTROLS_EVENTS_BY_CATEGORY.SMART_METRICS,
    ...ALTERNATIVE_DATES_EVENTS_BY_CATEGORY.SMART_METRICS,
  },
} as const satisfies Record<EventCategory, Record<string, Event>>;
